import ChangeElementTextAction from "./actions/ChangeElementTextAction";
import ChangeElementTextMergeableAction from "./actions/ChangeElementTextMergeableAction";
import ChangeSelectionAction from "./actions/ChangeSelectionAction";
import InsertElementAction from "./actions/InsertElementAction";
import RemoveElementAction from "./actions/RemoveElementAction";
import FnEditorSelection from "./FnEditorSelection";
function getOffsetInsideNode(node, selectedNode, selectedNodeOffset, currentOffset = 0) {
    if (node === selectedNode) {
        return [currentOffset + selectedNodeOffset, true];
    }
    let foundSelectedNode = false;
    if (node.nodeType === Node.TEXT_NODE) {
        const length = node.textContent ? node.textContent.length : 0;
        currentOffset += length;
    }
    else {
        node.childNodes.forEach((childNode) => {
            if (foundSelectedNode) {
                return;
            }
            [currentOffset, foundSelectedNode] = getOffsetInsideNode(childNode, selectedNode, selectedNodeOffset, currentOffset);
        });
    }
    return [currentOffset, foundSelectedNode];
}
function getNodeAndOffsetToSetSelection(node, offset) {
    if (offset === 0) {
        return [offset, node];
    }
    if (node.nodeType === Node.TEXT_NODE) {
        const length = node.textContent ? node.textContent.length : 0;
        if (length < offset) {
            offset -= length;
        }
        else {
            return [offset, node];
        }
    }
    else {
        for (let lp = 0; lp < node.childNodes.length; lp++) {
            let foundNode = null;
            [offset, foundNode] = getNodeAndOffsetToSetSelection(node.childNodes[lp], offset);
            if (foundNode) {
                return [offset, foundNode];
            }
        }
    }
    return [offset, null];
}
// Note that rendering the element nodes is done by the FnEditorElement class, so we always create empty divs here and never change the contents.
export default class FnEditorDom {
    constructor(editorRoot, identifier, editorIds, logger) {
        this.editorRoot = editorRoot;
        this.identifier = identifier;
        this.editorIds = editorIds;
        this.logger = logger;
        this.listeners = [];
        this.mutationEnabledId = null;
        this.mutationSelectionBefore = null;
        this.lastSelection = null;
        this.editorRoot.contentEditable = 'true';
        this.destroyer = this.init();
    }
    init() {
        const mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "characterData") {
                    let element = this.getParentElement(mutation.target);
                    if (!element && this.mutationEnabledId) { // When an element changes to blank, mutation.target is not set.
                        element = this.getById(this.mutationEnabledId);
                    }
                    if (element) {
                        const elementId = this.getIdFromElement(element);
                        if (this.mutationEnabledId !== elementId) {
                            return;
                        }
                        this.emitMergeableAction(new ChangeElementTextMergeableAction(elementId, element.textContent || '', this.mutationSelectionBefore));
                        return;
                    }
                }
            });
        });
        mutationObserver.observe(this.editorRoot, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: false
        });
        const keyDown = this.handleKeyDown.bind(this);
        const beforeInput = this.handleBeforeInput.bind(this);
        const paste = this.handlePaste.bind(this);
        const copy = this.handleCopy.bind(this);
        document.addEventListener('keydown', keyDown);
        this.editorRoot.addEventListener("beforeinput", beforeInput);
        this.editorRoot.addEventListener("paste", paste);
        this.editorRoot.addEventListener("copy", copy);
        const onSelectionChange = () => {
            this.lastSelection = this.getSelection();
        };
        document.addEventListener('selectionchange', onSelectionChange);
        const onTabEnable = () => {
            const restoreSelection = localStorage.getItem('lastSelection' + this.identifier);
            if (restoreSelection) {
                const restoreSelectionObj = FnEditorSelection.fromRaw(JSON.parse(restoreSelection));
                if (restoreSelectionObj) {
                    setTimeout(() => {
                        this.setSelection(restoreSelectionObj);
                    }, 100);
                }
            }
            const restoreScroll = localStorage.getItem('lastScroll' + this.identifier);
            if (restoreScroll) {
                const parent = this.getScrollParent(this.editorRoot);
                setTimeout(() => {
                    parent.scrollTop = parseInt(restoreScroll);
                }, 100);
            }
        };
        const blur = () => {
            if (this.lastSelection) {
                localStorage.setItem('lastSelection' + this.identifier, JSON.stringify(this.lastSelection.getRaw()));
            }
            else {
                localStorage.removeItem('lastSelection' + this.identifier);
            }
            const parent = this.getScrollParent(this.editorRoot);
            localStorage.setItem('lastScroll' + this.identifier, parent.scrollTop.toString());
        };
        document.addEventListener('tabEnable' + this.identifier, onTabEnable);
        this.editorRoot.addEventListener('blur', blur);
        return () => {
            mutationObserver.disconnect();
            document.removeEventListener('tabEnable' + this.identifier, onTabEnable);
            this.editorRoot.removeEventListener('blur', blur);
            document.removeEventListener('selectionchange', onSelectionChange);
            document.removeEventListener('keydown', keyDown);
            this.editorRoot.removeEventListener("beforeinput", beforeInput);
            this.editorRoot.removeEventListener("paste", paste);
            this.editorRoot.removeEventListener("copy", copy);
        };
    }
    destroy() {
        this.destroyer();
    }
    emitActionSet(actions) {
        this.listeners.forEach(listener => {
            listener.actionSet(actions);
        });
    }
    emitMergeableAction(action) {
        this.listeners.forEach(listener => {
            listener.mergeableAction(action);
        });
    }
    handleBeforeInput(e) {
        this.mutationEnabledId = null;
        this.mutationSelectionBefore = null;
        const selection = this.getSelection();
        if (selection && selection.multipleElementsSelected()) {
            const rangeSelectionInfo = this.getRangeSelectionInfo(selection);
            if (rangeSelectionInfo.fullySelectedIds.length === 0) {
                this.mutationEnabledId = (selection === null || selection === void 0 ? void 0 : selection.startId) || null;
                this.mutationSelectionBefore = selection;
                return; // let browser handle text removal
            }
            e.preventDefault();
            e.stopPropagation();
            const actionSet = [];
            let insertedText = e.data || '';
            let newCurrentText = insertedText;
            if (rangeSelectionInfo.startElementPosition) {
                const currentElement = this.getById(selection.startId);
                newCurrentText = (currentElement.textContent || '').substring(0, selection.startPosition) + newCurrentText;
                actionSet.push(new ChangeElementTextAction(selection.startId, newCurrentText));
            }
            rangeSelectionInfo.fullySelectedIds.forEach((id) => {
                if (id === selection.startId) {
                    actionSet.push(new ChangeElementTextAction(id, insertedText));
                    return;
                }
                actionSet.push(new RemoveElementAction(id));
            });
            if (rangeSelectionInfo.endElementPosition) {
                const endElement = this.getById(selection.endId);
                actionSet.push(new ChangeElementTextAction(selection.startId, newCurrentText + (endElement.textContent || '').substring(rangeSelectionInfo.endElementPosition)));
                actionSet.push(new RemoveElementAction(selection.endId));
            }
            actionSet.push(new ChangeSelectionAction(new FnEditorSelection(selection.startId, selection.startPosition + insertedText.length), selection));
            this.emitActionSet(actionSet);
        }
        else {
            this.mutationSelectionBefore = selection;
            this.mutationEnabledId = (selection === null || selection === void 0 ? void 0 : selection.startId) || null;
        }
    }
    handleCopy(e) {
        e.preventDefault();
        if (!e.clipboardData) {
            return;
        }
        const selection = this.getSelection();
        if (!selection || selection.isCursor()) {
            return;
        }
        const lines = [];
        const selectionInfo = this.getRangeSelectionInfo(selection);
        if (selectionInfo.startElementPosition) {
            const startElement = this.getById(selection.startId);
            let startElementText = (startElement.textContent || '').substring(selectionInfo.startElementPosition);
            if (selection.endId && selection.endId === selection.startId) {
                startElementText = (startElement.textContent || '').substring(selectionInfo.startElementPosition, selection.endPosition);
            }
            lines.push(startElementText);
        }
        selectionInfo.fullySelectedIds.forEach((id) => {
            const element = this.getById(id);
            const text = element.textContent || '';
            lines.push(text);
        });
        if (selectionInfo.endElementPosition) {
            const endElement = this.getById(selection.endId);
            const endElementText = endElement.textContent || '';
            lines.push(endElementText.substring(0, selectionInfo.endElementPosition));
        }
        e.clipboardData.setData("text/plain", lines.join("\n"));
        this.logger.log('Copy', lines);
    }
    handlePaste(e) {
        e.preventDefault();
        if (!e.clipboardData) {
            return;
        }
        const pastedText = (e.clipboardData).getData('text/plain');
        if (pastedText) {
            const lines = pastedText.split(/\r?\n/);
            this.logger.log('Pasting', lines);
            if (lines.length < 2 && lines[0] === '') {
                return;
            }
            const selection = this.getSelection();
            if (!selection) {
                const actions = [];
                let previousId;
                lines.forEach((line) => {
                    const newId = this.editorIds.createId();
                    actions.push(new InsertElementAction(newId, previousId, line));
                    previousId = newId;
                });
                this.emitActionSet(actions);
                return;
            }
            if (selection.isCursor()) {
                const actions = [];
                const firstLine = lines.shift();
                const elementToAddTo = this.getById(selection.startId);
                const elementToAddToText = (elementToAddTo.textContent || '').substring(0, selection.startPosition);
                const elementToAddToRestText = (elementToAddTo.textContent || '').substring(selection.startPosition);
                let newSelection = new FnEditorSelection(selection.startId, selection.startPosition + (firstLine || '').length);
                let newText = this.insertIntoString(elementToAddToText, firstLine || '', selection.startPosition);
                const lastLine = lines.pop();
                if (lastLine === undefined) {
                    newText += elementToAddToRestText;
                }
                actions.push(new ChangeElementTextAction(selection.startId, newText));
                let previousId = selection.startId;
                lines.forEach((line) => {
                    const newId = this.editorIds.createId();
                    actions.push(new InsertElementAction(newId, previousId, line));
                    previousId = newId;
                    newSelection = new FnEditorSelection(newId, line.length);
                });
                if (lastLine !== undefined) {
                    const newId = this.editorIds.createId();
                    const newText = lastLine + elementToAddToRestText;
                    actions.push(new InsertElementAction(newId, previousId, newText));
                    newSelection = new FnEditorSelection(newId, lastLine.length);
                }
                actions.push(new ChangeSelectionAction(newSelection, selection));
                this.emitActionSet(actions);
            }
            else {
                const actions = [];
                const selectionInfo = this.getRangeSelectionInfo(selection);
                let newSelection = selection;
                if (selectionInfo.startElementPosition) {
                    const startElement = this.getById(selection.startId);
                    const startElementText = startElement.textContent || '';
                    const shortenedText = startElementText.substring(0, selection.startPosition);
                    const firstLine = lines.shift();
                    let newText = shortenedText + firstLine;
                    newSelection = new FnEditorSelection(selection.startId, newText.length);
                    if (lines.length === 0 && selectionInfo.endElementPosition) {
                        // rest from end element
                        const endElement = this.getById(selection.endId);
                        const endText = endElement.textContent || '';
                        const addedText = endText.substring(selectionInfo.endElementPosition);
                        newText += addedText;
                        actions.push(new RemoveElementAction(selection.endId));
                    }
                    actions.push(new ChangeElementTextAction(selection.startId, newText));
                }
                let lastLine;
                if (selectionInfo.endElementPosition) {
                    lastLine = lines.pop();
                }
                let previousId = selection.startId;
                if (!selectionInfo.startElementPosition) {
                    // start element will be deleted
                    const previousElement = this.getById(selection.startId).previousElementSibling;
                    previousId = previousElement ? this.getIdFromElement(previousElement) : undefined;
                }
                selectionInfo.fullySelectedIds.forEach((id) => {
                    actions.push(new RemoveElementAction(id));
                });
                lines.forEach((line) => {
                    const newId = this.editorIds.createId();
                    actions.push(new InsertElementAction(newId, previousId, line));
                    previousId = newId;
                    newSelection = new FnEditorSelection(newId, line.length);
                });
                if (lastLine !== undefined && selectionInfo.endElementPosition) {
                    const endElement = this.getById(selection.endId);
                    const endText = endElement.textContent || '';
                    const newEndText = lastLine + endText.substring(selectionInfo.endElementPosition, endText.length);
                    newSelection = new FnEditorSelection(selection.endId, lastLine.length);
                    actions.push(new ChangeElementTextAction(selection.endId, newEndText));
                }
                actions.push(new ChangeSelectionAction(newSelection, selection));
                this.emitActionSet(actions);
            }
        }
    }
    insertIntoString(original, toInsert, position) {
        if (position < 0 || position > original.length) {
            throw new Error('Position is out of bounds');
        }
        return original.slice(0, position) + toInsert + original.slice(position);
    }
    handleKeyDown(e) {
        var _a;
        if (e.ctrlKey && e.key.toLowerCase() === 'z') {
            e.preventDefault();
            e.stopPropagation();
            if (e.shiftKey) {
                e.stopPropagation();
                e.preventDefault();
                this.listeners.forEach((listener) => {
                    listener.redo();
                });
            }
            else {
                e.stopPropagation();
                e.preventDefault();
                this.listeners.forEach((listener) => {
                    listener.undo();
                });
            }
            return;
        }
        if (e.ctrlKey && e.key.toLowerCase() === 's') {
            e.preventDefault();
            e.stopPropagation();
            // SAVE
            return;
        }
        if (e.ctrlKey && e.key.toLowerCase() === 'h') {
            e.preventDefault();
            e.stopPropagation();
            this.listeners.forEach((listener) => {
                listener.showHistory();
            });
            return;
        }
        if (e.ctrlKey && e.key.toLowerCase() === 'q') {
            e.preventDefault();
            e.stopPropagation();
            this.listeners.forEach((listener) => {
                listener.showNavigator();
            });
            return;
        }
        if (e.target) {
            const target = e.target;
            if (this.editorRoot !== target && !this.editorRoot.contains(target)) {
                // outside editor
                return;
            }
        }
        const selection = this.getSelection();
        switch (e.key) {
            case 'Enter':
                e.preventDefault();
                e.stopPropagation();
                if (selection) {
                    if (selection.isCursor()) {
                        if (selection.startPosition === 0) { // Cursor at beginning of line, insert element above
                            const afterId = (_a = this.getById(selection.startId).previousElementSibling) === null || _a === void 0 ? void 0 : _a.getAttribute('data-id');
                            this.emitActionSet([
                                new InsertElementAction(this.editorIds.createId(), afterId ? afterId : undefined),
                                new ChangeSelectionAction(selection, selection)
                            ]);
                            return;
                        }
                        const elementText = this.getById(selection.startId).textContent || '';
                        if (selection.startPosition === elementText.length) { // Cursor at end of line, insert element after
                            const actionSet = [];
                            let additionalLineId;
                            if (!e.shiftKey) {
                                const element = this.getById(selection.startId);
                                if (element.classList.contains('fn-editor-element--scene-heading') || element.classList.contains('fn-editor-element--dialogue') || element.classList.contains('fn-editor-element-intrinsic--transition')) {
                                    const nextElement = element.nextElementSibling;
                                    if (nextElement && !nextElement.classList.contains('fn-editor-element--scene-dialogue')) {
                                        // two lines
                                        additionalLineId = this.editorIds.createId();
                                        actionSet.push(new InsertElementAction(additionalLineId, selection.startId));
                                    }
                                }
                            }
                            const newId = this.editorIds.createId();
                            this.emitActionSet([
                                ...actionSet,
                                new InsertElementAction(newId, additionalLineId !== null && additionalLineId !== void 0 ? additionalLineId : selection.startId),
                                new ChangeSelectionAction(new FnEditorSelection(newId, 0), selection),
                            ]);
                            return;
                        }
                        // Cursor in the middle: split element
                        const newElementId = this.editorIds.createId();
                        const currentElement = this.getById(selection.startId);
                        const currentText = currentElement.textContent || '';
                        const newCurrentText = currentText.substring(0, selection.startPosition);
                        const newElementText = currentText.substring(selection.startPosition);
                        this.emitActionSet([
                            new ChangeElementTextAction(selection.startId, newCurrentText),
                            new InsertElementAction(newElementId, selection.startId, newElementText),
                            new ChangeSelectionAction(new FnEditorSelection(newElementId, 0), selection)
                        ]);
                        return;
                    }
                    // If selection range, REMOVE selection and then insert at new cursor position
                    const rangeSelectionInfo = this.getRangeSelectionInfo(selection);
                    const actionSet = [];
                    if (rangeSelectionInfo.startElementPosition) {
                        const currentElement = this.getById(selection.startId);
                        const newCurrentText = (currentElement.textContent || '').substring(0, selection.startPosition);
                        actionSet.push(new ChangeElementTextAction(selection.startId, newCurrentText));
                    }
                    const newElementId = this.editorIds.createId();
                    actionSet.push(new InsertElementAction(newElementId, selection.startId, ''));
                    rangeSelectionInfo.fullySelectedIds.forEach((id) => {
                        actionSet.push(new RemoveElementAction(id));
                    });
                    if (rangeSelectionInfo.endElementPosition) {
                        const endElement = this.getById(selection.endId);
                        actionSet.push(new ChangeElementTextAction(selection.endId, (endElement.textContent || '').substring(rangeSelectionInfo.endElementPosition)));
                    }
                    actionSet.push(new ChangeSelectionAction(new FnEditorSelection(newElementId, 0), selection));
                    this.emitActionSet(actionSet);
                }
                return;
            case 'Backspace':
                if (selection) {
                    if (selection.isCursor() && selection.startPosition === 0) {
                        e.preventDefault();
                        e.stopPropagation();
                        // if cursor at 0 and element is empty, remove and place cursor at end of previous element
                        const element = this.getById(selection.startId);
                        const previousElement = element.previousElementSibling;
                        if (!previousElement) {
                            return;
                        }
                        const previousId = this.getIdFromElement(previousElement);
                        const previousText = previousElement.textContent || '';
                        const elementText = element.textContent || '';
                        if ('' === elementText) {
                            this.emitActionSet([
                                new RemoveElementAction(selection.startId),
                                new ChangeSelectionAction(new FnEditorSelection(previousId, previousText.length), selection)
                            ]);
                            return;
                        }
                        // if cursor at 0 and element is not empty, move text into previous element
                        const newText = previousText + elementText;
                        this.emitActionSet([
                            new ChangeElementTextAction(previousId, newText),
                            new RemoveElementAction(selection.startId),
                            new ChangeSelectionAction(new FnEditorSelection(previousId, previousText.length), selection)
                        ]);
                        return;
                    }
                    else if (!selection.isCursor()) {
                        const rangeSelectionInfo = this.getRangeSelectionInfo(selection);
                        if (rangeSelectionInfo.fullySelectedIds.length === 0) {
                            return; // let browser handle text removal
                        }
                        e.preventDefault();
                        e.stopPropagation();
                        // if range selection, REMOVE selection
                        const actionSet = [];
                        let newCurrentText = '';
                        if (rangeSelectionInfo.startElementPosition) {
                            const currentElement = this.getById(selection.startId);
                            newCurrentText = (currentElement.textContent || '').substring(0, selection.startPosition);
                            actionSet.push(new ChangeElementTextAction(selection.startId, newCurrentText));
                        }
                        rangeSelectionInfo.fullySelectedIds.forEach((id) => {
                            if (id === selection.startId) {
                                actionSet.push(new ChangeElementTextAction(id, ''));
                                return;
                            }
                            actionSet.push(new RemoveElementAction(id));
                        });
                        if (rangeSelectionInfo.endElementPosition) {
                            const endElement = this.getById(selection.endId);
                            actionSet.push(new ChangeElementTextAction(selection.startId, newCurrentText + (endElement.textContent || '').substring(rangeSelectionInfo.endElementPosition)));
                            actionSet.push(new RemoveElementAction(selection.endId));
                        }
                        actionSet.push(new ChangeSelectionAction(new FnEditorSelection(selection.startId, selection.startPosition), selection));
                        this.emitActionSet(actionSet);
                    }
                }
                return;
            case 'Delete':
                if (selection) {
                    if (selection.isCursor()) {
                        const element = this.getById(selection.startId);
                        const elementText = element.textContent || '';
                        if (selection.startPosition === elementText.length) {
                            e.preventDefault();
                            e.stopPropagation();
                            // if cursor at end and element is empty, remove and place cursor at beginning of next element
                            const nextElement = element.nextElementSibling;
                            if (!nextElement) {
                                return;
                            }
                            const nextId = this.getIdFromElement(nextElement);
                            if ('' === elementText) {
                                this.emitActionSet([
                                    new RemoveElementAction(selection.startId),
                                    new ChangeSelectionAction(new FnEditorSelection(nextId, 0), selection)
                                ]);
                                return;
                            }
                            // if cursor at end and element is not empty, move next text into this element
                            const nextText = nextElement.textContent || '';
                            const newText = elementText + nextText;
                            this.emitActionSet([
                                new ChangeElementTextAction(selection.startId, newText),
                                new RemoveElementAction(nextId),
                                new ChangeSelectionAction(new FnEditorSelection(selection.startId, selection.startPosition), selection)
                            ]);
                            return;
                        }
                    }
                    else {
                        // same as backspace:
                        const rangeSelectionInfo = this.getRangeSelectionInfo(selection);
                        if (rangeSelectionInfo.fullySelectedIds.length === 0) {
                            return; // let browser handle text removal
                        }
                        e.preventDefault();
                        e.stopPropagation();
                        // if range selection, REMOVE selection
                        const actionSet = [];
                        let newCurrentText = '';
                        if (rangeSelectionInfo.startElementPosition) {
                            const currentElement = this.getById(selection.startId);
                            newCurrentText = (currentElement.textContent || '').substring(0, selection.startPosition);
                            actionSet.push(new ChangeElementTextAction(selection.startId, newCurrentText));
                        }
                        rangeSelectionInfo.fullySelectedIds.forEach((id) => {
                            if (id === selection.startId) {
                                actionSet.push(new ChangeElementTextAction(id, ''));
                                return;
                            }
                            actionSet.push(new RemoveElementAction(id));
                        });
                        if (rangeSelectionInfo.endElementPosition) {
                            const endElement = this.getById(selection.endId);
                            actionSet.push(new ChangeElementTextAction(selection.startId, newCurrentText + (endElement.textContent || '').substring(rangeSelectionInfo.endElementPosition)));
                            actionSet.push(new RemoveElementAction(selection.endId));
                        }
                        actionSet.push(new ChangeSelectionAction(new FnEditorSelection(selection.startId, selection.startPosition), selection));
                        this.emitActionSet(actionSet);
                    }
                }
                return;
            case 'ArrowRight':
            case 'ArrowLeft':
            case 'ArrowUp':
            case 'ArrowDown':
                // Just keep native behaviour in contentEditable
                return;
        }
    }
    getIdFromElement(domElement) {
        var _a;
        return (_a = domElement.getAttribute('data-id')) !== null && _a !== void 0 ? _a : '';
    }
    getParentElement(node) {
        const isBlockElement = node.nodeType === Node.ELEMENT_NODE && node.classList.contains('fn-editor-element');
        if (isBlockElement) {
            return node;
        }
        if (!node.parentElement) {
            return null;
        }
        return this.getParentElement(node.parentElement);
    }
    addListener(listener) {
        this.listeners.push(listener);
    }
    clear() {
        this.editorRoot.innerHTML = '';
    }
    createElement(id) {
        const newElement = document.createElement('div');
        newElement.setAttribute('data-id', id);
        return newElement;
    }
    insertAfter(idToInsert, afterId) {
        const newElement = this.createElement(idToInsert);
        if (!afterId) {
            this.editorRoot.prepend(newElement);
        }
        else {
            this.getById(afterId).insertAdjacentElement('afterend', newElement);
        }
        return newElement;
    }
    insertLast(idToInsert) {
        const newElement = this.createElement(idToInsert);
        this.editorRoot.append(newElement);
        return newElement;
    }
    getById(id) {
        const element = this.editorRoot.querySelector(`[data-id="${id}"]`);
        if (!element) {
            throw new Error(`Out of sync: ID ${id} not found.`);
        }
        return element;
    }
    removeById(id) {
        var _a;
        (_a = this.getById(id)) === null || _a === void 0 ? void 0 : _a.remove();
    }
    getSelection() {
        const doc = this.editorRoot.ownerDocument;
        const window = doc.defaultView;
        if (!window) {
            return null;
        }
        const selection = window.getSelection();
        if (!selection) {
            return null;
        }
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const startElement = this.getParentElement(range.startContainer);
            if (!startElement) {
                return null;
            }
            const [startOffset, foundSelectedStartNode] = getOffsetInsideNode(startElement, range.startContainer, range.startOffset, 0);
            if (!foundSelectedStartNode) {
                throw new Error(`Selected start node was not found.`);
            }
            const endElement = this.getParentElement(range.endContainer);
            if (!endElement) {
                return null;
            }
            const [endOffset, foundSelectedEndNode] = getOffsetInsideNode(endElement, range.endContainer, range.endOffset, 0);
            if (!foundSelectedEndNode) {
                throw new Error(`Selected end node was not found.`);
            }
            const startId = startElement.getAttribute('data-id');
            if (!startId) {
                throw new Error('Start element ID not found.');
            }
            const endId = endElement.getAttribute('data-id');
            if (!endId) {
                throw new Error('End element ID not found.');
            }
            return new FnEditorSelection(startId, startOffset, endId, endOffset);
        }
        return null;
    }
    scrollTo(id) {
        const startNode = this.getById(id);
        startNode.scrollIntoView({
            block: 'start',
            inline: 'nearest',
            behavior: 'auto'
        });
    }
    setSelection(selectionToSet) {
        const startNode = this.getById(selectionToSet.startId);
        const range = new Range();
        const [offset, node] = getNodeAndOffsetToSetSelection(startNode, selectionToSet.startPosition);
        if (node) {
            range.setStart(node, offset);
        }
        if (!selectionToSet.endId || (selectionToSet.endId === selectionToSet.startId && selectionToSet.startPosition === selectionToSet.endPosition)) {
            range.collapse(true);
        }
        else if (selectionToSet.endPosition) {
            const endNode = this.getById(selectionToSet.endId);
            const [offset, node] = getNodeAndOffsetToSetSelection(endNode, selectionToSet.endPosition);
            if (node) {
                range.setEnd(node, offset);
            }
        }
        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
        }
        if (selection === null || selection === void 0 ? void 0 : selection.isCollapsed) {
            const rect = range.getBoundingClientRect();
            const element = this.getById(selectionToSet.startId);
            let bottom = rect.bottom;
            if (rect.height === 0) {
                bottom = element.getBoundingClientRect().bottom;
            }
            if (bottom + 22 > window.innerHeight) {
                element.scrollIntoView(false);
                const scrollParent = this.getScrollParent(this.editorRoot);
                if (scrollParent) {
                    scrollParent.scrollTop += 22; // add line height
                }
            }
        }
    }
    getScrollParent(node) {
        if (node === null) {
            return null;
        }
        if (node.scrollHeight > node.clientHeight) {
            return node;
        }
        else {
            return this.getScrollParent(node.parentElement);
        }
    }
    getRangeSelectionInfo(selection) {
        var _a;
        if (selection.isCursor()) {
            throw new Error('Seletion is not a range');
        }
        if (selection.startId === selection.endId) {
            const element = this.getById(selection.startId);
            const textLength = ((_a = element.textContent) === null || _a === void 0 ? void 0 : _a.length) || 0;
            if (textLength === selection.endPosition && selection.startPosition === 0) {
                return {
                    fullySelectedIds: [selection.startId]
                };
            }
        }
        const inBetweenIds = [];
        let startIsCompletelySelected = (0 === selection.startPosition && selection.endId && selection.endId !== selection.startId);
        const endElement = this.getById(selection.endId);
        let startElementPosition;
        if (startIsCompletelySelected) {
            inBetweenIds.push(selection.startId);
        }
        else {
            startElementPosition = selection.startPosition;
        }
        if (selection.endId && selection.startId !== selection.endId) {
            const endElement = this.getById(selection.endId);
            let currentElement = this.getById(selection.startId).nextElementSibling;
            while (currentElement) {
                if (currentElement === endElement) {
                    break;
                }
                const id = this.getIdFromElement(currentElement);
                if (!id) {
                    throw new Error('Found element in in between start and end that had no ID.');
                }
                inBetweenIds.push(id);
                currentElement = currentElement.nextElementSibling;
            }
        }
        let endElementPosition;
        if (selection.endId && selection.endId !== selection.startId) {
            let endIsCompletelySelected = (endElement.textContent ? endElement.textContent.length : 0) <= (selection.endPosition || 0);
            if (endIsCompletelySelected) {
                if (!inBetweenIds.includes(selection.endId)) {
                    inBetweenIds.push(selection.endId);
                }
            }
            else {
                endElementPosition = selection.endPosition;
            }
        }
        return {
            startElementPosition,
            fullySelectedIds: inBetweenIds,
            endElementPosition,
        };
    }
}
